import React from "react";
//#region types
import { FileViewerFileData, ImageMeta } from "models/propTypes/fileViewerPropTypes";
import { AuditSheetFile } from "../widgets/auditSheetFiles";
//#endregion
//#region components
import { FileViewerContainer } from "components/common";
import { useAuditService } from "serviceHooks";
//#endregion

interface AuditSheetFileViewerStart {
  key: number;
  index: number;
}

function useAuditSheetFileViewer(
  auditSheetId: number,
  files: AuditSheetFile[],
  start: AuditSheetFileViewerStart,
  onCurrentIndexChange: (index: number) => void
) {
  const auditService = useAuditService();
  // The viewer asks for the same file more than once while mounting and on every remount.
  const fileRequests = React.useMemo(() => new Map<number, Promise<FileViewerFileData>>(), [auditSheetId, files]);

  const meta = React.useMemo(
    () =>
      files.map(
        (file): ImageMeta => ({
          id: file.attachmentId,
          fileName: file.fileName,
          contentType: file.contentType,
          numberOfPages: 1,
        })
      ),
    [files]
  );

  const getFile = (_fileId: number | string, index: number) => {
    const file = files[index];
    if (!file) return Promise.resolve<FileViewerFileData>({ data: "" });

    let request = fileRequests.get(index);
    if (!request) {
      request = auditService
        .getAttachment(auditSheetId, file.attachmentId, file.wintAccountingVoucherId)
        .then((attachment) => ({ data: attachment.data ?? "", contentType: attachment.contentType }));
      request.catch(() => fileRequests.delete(index));
      fileRequests.set(index, request);
    }
    return request;
  };

  return files.length > 0 ? (
    <FileViewerContainer
      key={start.key}
      meta={meta}
      parentId={auditSheetId}
      initialIndex={start.index}
      onCurrentIndexChange={onCurrentIndexChange}
      getFile={getFile}
    />
  ) : undefined;
}

export default useAuditSheetFileViewer;
